import { Box, Flex, HStack, Input, Radio, RadioGroup, Stack, Text } from '@chakra-ui/react';
import { HelperTooltip } from 'components/Tooltip';
import PropTypes from 'prop-types';
import { HILL_PLAN_MONTH_LEARNING_TOOLTIP } from './calc/hillPlanMonthLearning';
import { FUTURE_FUNDING_ROAS_SOURCE } from './calc/resolveUseHillRoasForFunding';

const FUTURE_FUNDING_ROAS_TOOLTIP = (
  <>
    <Text mb={2}>
      Which curve drives revenue and repayments for new marketing months.
    </Text>
    <Text>
      When a Hill model is fit from cohort data, new marketing months normally use
      spend-based ROAS. Choose <strong>General ROAS vector</strong> to use the
      editable cumulative curve above for all future funding instead.
    </Text>
  </>
);

const HILL_MODE_TOOLTIP = (
  <Text>
    Spend-based ROAS from the cohort Hill fit for each marketing month (higher spend
    → lower cumulative ROAS at each month-on-book).
  </Text>
);

const GENERAL_MODE_TOOLTIP = (
  <Text>
    Use the editable cumulative ROAS curve for all future funding months, while
    keeping the Hill fit and cohort table for reference.
  </Text>
);

const HILL_LEARNING_FACTOR_TOOLTIP = HILL_PLAN_MONTH_LEARNING_TOOLTIP;

function FutureFundingRoasSection({
  source,
  onChangeSource,
  learningFactor,
  onChangeLearningFactor,
  dense = false,
  stacked = false,
}) {
  const usingHill = source === FUTURE_FUNDING_ROAS_SOURCE.HILL;

  const modeRadios = (
    <>
      <Flex align="center" gap={0}>
        <Radio value={FUTURE_FUNDING_ROAS_SOURCE.HILL} size="sm">
          Hill function
        </Radio>
        <HelperTooltip content={HILL_MODE_TOOLTIP} />
      </Flex>
      <Flex align="center" gap={0}>
        <Radio value={FUTURE_FUNDING_ROAS_SOURCE.GENERAL} size="sm">
          General ROAS
        </Radio>
        <HelperTooltip content={GENERAL_MODE_TOOLTIP} />
      </Flex>
    </>
  );

  const learningFactorField = usingHill ? (
    <Flex align="center" gap={1} mt={stacked ? 1 : 0}>
      <Text fontSize="xs">Learning factor</Text>
      <HelperTooltip content={HILL_LEARNING_FACTOR_TOOLTIP} />
      <Input
        type="number"
        size="sm"
        w="88px"
        min={0}
        step={0.001}
        textAlign="right"
        value={learningFactor ?? 0.01}
        onChange={(e) => {
          const raw = e.target.value.trim();
          if (raw === '') {
            onChangeLearningFactor(0);
            return;
          }
          const parsed = Number(raw);
          onChangeLearningFactor(
            Number.isFinite(parsed) && parsed >= 0 ? parsed : 0
          );
        }}
      />
    </Flex>
  ) : null;

  if (stacked) {
    return (
      <Box>
        <Flex align="center" gap={1} mb={1}>
          <Text fontSize="xs" fontWeight="semibold" color="gray.600">
            Future funding ROAS
          </Text>
          <HelperTooltip content={FUTURE_FUNDING_ROAS_TOOLTIP} />
        </Flex>
        <RadioGroup value={source} onChange={onChangeSource} colorScheme="purple">
          <HStack spacing={3} align="flex-start" flexWrap="wrap">
            {modeRadios}
          </HStack>
        </RadioGroup>
        {learningFactorField}
      </Box>
    );
  }

  return (
    <Box mt={dense ? 1 : 3}>
      <Flex align="center" gap={4} flexWrap="wrap">
        <Flex align="center" gap={1} flexShrink={0}>
          <Text fontSize="sm" fontWeight="medium">
            Future funding ROAS
          </Text>
          <HelperTooltip content={FUTURE_FUNDING_ROAS_TOOLTIP} />
        </Flex>

        <RadioGroup
          value={source}
          onChange={onChangeSource}
          colorScheme="purple"
        >
          <HStack spacing={4}>{modeRadios}</HStack>
        </RadioGroup>

        {learningFactorField}
      </Flex>
    </Box>
  );
}

FutureFundingRoasSection.propTypes = {
  source: PropTypes.oneOf([
    FUTURE_FUNDING_ROAS_SOURCE.HILL,
    FUTURE_FUNDING_ROAS_SOURCE.GENERAL,
  ]).isRequired,
  onChangeSource: PropTypes.func.isRequired,
  learningFactor: PropTypes.number,
  onChangeLearningFactor: PropTypes.func.isRequired,
  dense: PropTypes.bool,
  stacked: PropTypes.bool,
};

export default FutureFundingRoasSection;
